import { useCallback, useEffect, useRef, useState } from 'react';
import axios, { AxiosPromise, CancelToken, CancelTokenSource } from 'axios';
import { getShowNewApi } from 'app/services/showApi';

export interface CallbackOptions<R> {
  onBefore?: () => void | boolean;
  onSuccess?: (response: R) => void;
  onError?: (error: any) => void;
  onFinally?: () => void;
}

export interface GeneralConfig {
  payloadData?: any;
  IIFE?: boolean;
  takeFirstRequest?: boolean;
  cacheDurationMs?: number; // optional cache duration in ms; if set (>0) responses are cached per payload
}

// Simple in-memory cache shared across hook instances: Map<ActionAPI, Map<string, { ts, data }>>
const cacheStore: Map<ActionAPI<any>, Map<string, { ts: number; data: any }>> = new Map();

export type PublicExtendable<R> = GeneralConfig & CallbackOptions<R>;

type ActionAPI<R> = (
  data: any,
  config: { cancelToken: CancelToken },
) => AxiosPromise<R>;

export interface Params<R> extends PublicExtendable<R> {
  actionApi: ActionAPI<R>;
}

const useFetchData = <R>(params: Params<R>) => {
  const {
    actionApi,
    IIFE,
    takeFirstRequest,
    onBefore,
    onSuccess,
    onError,
    payloadData,
    onFinally,
  } = params;
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef<boolean>(false);
  const [result, setResult] = useState<R>();
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  const onFetchData = useCallback(
    async (additionalOptions?: {
      payloadData?: any;
      callback?: CallbackOptions<R>;
    }) => {
      // Use ref to read latest loading state without recreating callback on every state change
      if (takeFirstRequest && isLoadingRef.current) return;
      if (cancelTokenRef.current) cancelTokenRef.current.cancel();
      cancelTokenRef.current = axios.CancelToken.source();

      const PayloadData = additionalOptions?.payloadData ?? payloadData;
      const OnBefore = additionalOptions?.callback?.onBefore ?? onBefore;
      const OnSuccess = additionalOptions?.callback?.onSuccess ?? onSuccess;
      const OnError = additionalOptions?.callback?.onError ?? onError;
      const OnFinally = additionalOptions?.callback?.onFinally ?? onFinally;
      const cacheDurationMs = additionalOptions?.callback?.onFinally ? undefined : (params as any)?.cacheDurationMs || (params as any)?.cacheDurationMs; // placeholder to keep TS happy (we'll read from params below)

      try {
        const beforeResult = OnBefore?.();
        if (beforeResult === false) return;

        // Check cache (use params.cacheDurationMs if provided)
        const ttl = (params as any)?.cacheDurationMs ?? 0;
        const cacheKey = JSON.stringify(PayloadData ?? '');
        if (ttl > 0) {
          let actionCache = cacheStore.get(actionApi as any);
          if (actionCache) {
            const entry = actionCache.get(cacheKey);
            if (entry && Date.now() - entry.ts < ttl) {
              // Return cached result synchronously
              OnSuccess?.(entry.data);
              setResult(entry.data);
              return entry.data;
            }
          }
        }

        isLoadingRef.current = true;
        setIsLoading(true);
        const response = await actionApi(PayloadData, {
          cancelToken: cancelTokenRef.current.token,
        });
        const responseData = response.data;

        // Store in cache if ttl provided
        if ((params as any)?.cacheDurationMs && (params as any)?.cacheDurationMs > 0) {
          const ttlStore = (params as any)?.cacheDurationMs as number;
          const cacheKey2 = JSON.stringify(PayloadData ?? '');
          let actionCache = cacheStore.get(actionApi as any);
          if (!actionCache) {
            actionCache = new Map<string, { ts: number; data: any }>();
            cacheStore.set(actionApi as any, actionCache);
          }
          actionCache.set(cacheKey2, { ts: Date.now(), data: responseData });
        }

        OnSuccess?.(responseData);
        setResult(responseData);
        isLoadingRef.current = false;
        setIsLoading(false);
        return responseData;
      } catch (err: any) {
        OnError?.(err);
        if (err?.code !== 'ERR_CANCELED') {
          isLoadingRef.current = false;
          setIsLoading(false);
        } else {
          // Cancel may also end loading
          isLoadingRef.current = false;
        }
      } finally {
        OnFinally?.();
      }
    },
    [
      actionApi,
      onBefore,
      onError,
      onFinally,
      onSuccess,
      payloadData,
      takeFirstRequest,
    ],
  );

  useEffect(() => {
    if (IIFE === undefined || IIFE) {
      const onFetchData = async () => {
        if (cancelTokenRef.current) cancelTokenRef.current.cancel();
        cancelTokenRef.current = axios.CancelToken.source();

        try {
          const beforeResult = onBefore?.();
          if (beforeResult === false) return;
          isLoadingRef.current = true;
          setIsLoading(true);
          const response = await actionApi(payloadData, {
            cancelToken: cancelTokenRef.current.token,
          });
          const responseData = response.data;
          onSuccess?.(responseData);
          setResult(responseData);
          isLoadingRef.current = false;
          setIsLoading(false);
          return responseData;
        } catch (err: any) {
          onError?.(err);
          if (err?.code !== 'ERR_CANCELED') {
            isLoadingRef.current = false;
            setIsLoading(false);
          } else {
            isLoadingRef.current = false;
          }
        } finally {
          onFinally?.();
        }
      };

      onFetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IIFE, payloadData]);

  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) cancelTokenRef.current.cancel();
    };
  }, []);

  return {
    isLoading,
    onFetchData,
    result,
  };
};

export default useFetchData;
